import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, DEMO_MODE } from '../utils/api';
import { useVoice } from '../context/VoiceContext';
import AIDisclaimer from '../components/AIDisclaimer';
import InfoTip from '../components/InfoTip';
import {
  normalizeParsed,
  buildFormPatch,
  describeRecognition,
  normalizeValidation,
} from '../utils/aiParse';

const FREQUENCIES = ['每日1次', '每日2次', '每日3次', '每周1次', '隔日1次'];
// 单位表要和解析层（UNIT_PATTERN）对齐：解析能认出「丸（速效救心丸）、支（口服液）、
// 克（三七粉）、滴、微克」，下拉里却没有对应选项的话，select 会静默退回显示第一个
// 选项「片」，而表单里存的还是「丸」—— 用户看到的和实际保存的不是一回事（2026-09-22 补）。
const UNITS = ['片', '粒', '袋', '丸', '支', '滴', '毫升', '毫克', '克', '微克'];
const METHODS = ['饭前', '饭后', '空腹', '睡前'];

/**
 * 适老化的核心不是「字大」，是「能点就别打字」。
 * 老人打字慢、容易点错，而吃药这件事的答案是高度收敛的：
 * 药就那么几种、一次就吃 1~5 片、时间就早中晚睡前四个点。
 * 所以这三组预设覆盖绝大多数场景，输入框只留给例外。
 */
const COMMON_MEDS = [
  '阿司匹林肠溶片',
  '苯磺酸氨氯地平片',
  '二甲双胍缓释片',
  '阿托伐他汀钙片',
  '碳酸钙D3片',
  '维生素C片',
];
const DOSE_PRESETS = ['1', '2', '3', '4', '5'];
const TIME_PRESETS = [
  { label: '早晨', time: '08:00' },
  { label: '中午', time: '12:00' },
  { label: '晚上', time: '18:00' },
  { label: '睡前', time: '21:00' },
];

const EMPTY_FORM = {
  name: '',
  dosage: '',
  unit: '片',
  frequency: '每日1次',
  times: ['08:00'],
  method: '',
  notes: '',
};

/**
 * 读取图片并压缩后再交给 OCR。
 *
 * 手机原图动辄 4000px、5MB 以上：既可能超过服务端 10MB 的 body 限制，
 * 又会让 OCR 明显变慢甚至超时。压到长边 1600px 后识别速度与成功率都更好。
 * 压缩失败时退回原图，不阻断流程。
 */
function readImageAsCompressedDataURL(file, maxSide = 1600, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('图片读取失败，请重试'));
    reader.onload = () => {
      const original = reader.result;
      const img = new Image();
      img.onerror = () => reject(new Error('图片格式无法解析，请换一张'));
      img.onload = () => {
        try {
          const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
          if (scale >= 1 && file.size < 1.5 * 1024 * 1024) {
            resolve(original);
            return;
          }
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        } catch (e) {
          resolve(original);
        }
      };
      img.src = original;
    };
    reader.readAsDataURL(file);
  });
}

function getSpeechRecognition() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export default function AddMedicine() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const fileRef = useRef(null);
  const { speak } = useVoice();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [voiceRaw, setVoiceRaw] = useState('');
  const [textInput, setTextInput] = useState('');
  const [recognizing, setRecognizing] = useState(false);
  const [photoPreview, setPhotoPreview] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [recognizeInfo, setRecognizeInfo] = useState(null);
  const [micSupported] = useState(() => !!getSpeechRecognition());

  // 表单数据
  const [form, setForm] = useState(EMPTY_FORM);

  // 核对结果（规则校验的返回值，见 utils/aiParse.js 注释）
  const [validation, setValidation] = useState(null);

  const setField = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  /** 快捷时间：已选则取消，未选则加入并保持升序 */
  const toggleTime = (preset) => {
    const has = form.times.includes(preset.time);
    if (has && form.times.length <= 1) {
      setError('至少要保留一个提醒时间');
      return;
    }
    setError('');
    setField('times', has
      ? form.times.filter(t => t !== preset.time)
      : [...form.times, preset.time].sort());
    // 「睡前」既是一个时间点也是一种服用方式，顺手补上，省得老人再点一次
    if (!has && preset.label === '睡前' && !form.method) {
      setField('method', '睡前');
      if (!form.notes.trim()) setField('notes', '睡前服用');
    }
  };

  // 编辑模式：加载已有药品数据
  useEffect(() => {
    if (!isEdit) return;
    api.getMedicine(id)
      .then(med => {
        let times = [];
        try { times = JSON.parse(med.times); } catch (e) { times = []; }
        if (!Array.isArray(times) || times.length === 0) times = ['08:00'];
        setForm({
          name: med.name || '',
          dosage: med.dosage || '',
          unit: med.unit || '片',
          frequency: med.frequency || '每日1次',
          times,
          method: med.method || '',
          notes: med.notes || '',
        });
        speak(`正在编辑药品${med.name || ''}`);
      })
      .catch(err => setError('加载药品失败：' + err.message));
  }, [isEdit, id, speak]);

  /**
   * 统一的「识别结果落表」入口。
   *
   * 拍照 OCR 与语音/文字解析最终都汇到这里，保证两条链路行为一致：
   *  - 归一化（把 "100mg" 拆成剂量 100 + 单位 毫克，否则表单校验必然失败）
   *  - 只覆盖识别到的字段（不冲掉用户已手填的内容）
   *  - 如实告知识别到什么、漏了什么
   *  - 顺带跑一次规则核对（不是 AI 模型，是正则 + 人工整理的参考库，见 utils/aiParse.js）
   */
  const applyRecognition = async (parsedRaw, rawText, source) => {
    const parsed = normalizeParsed(parsedRaw, rawText);
    const { got, missing } = describeRecognition(parsed);
    const merged = { ...form, ...buildFormPatch(parsed) };

    setRecognizeInfo({ source, text: parsed.rawText, got, missing });
    setForm(merged);

    if (got.length === 0) {
      setError(
        source === 'ocr'
          ? '没能从图片中识别出药品信息，请对准药盒或处方单上的文字重拍，或直接用手动录入'
          : '没能从这段话中识别出药品信息，请说得更完整一些，例如「每天早上饭后吃一片阿司匹林」'
      );
      return;
    }
    setError('');

    // 有药品名才做核对，否则比对没有意义
    if (merged.name) {
      try {
        const valResult = await api.validateMedicine(merged);
        setValidation(normalizeValidation(valResult));
      } catch (e) {
        // 核对失败不应阻断录入，静默降级
      }
    }
  };

  // 拍照录入
  const handlePhoto = async (e) => {
    const file = e.target.files[0];
    // 允许重复选择同一张图
    e.target.value = '';
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      setError('请选择图片文件（jpg / png）');
      return;
    }
    setLoading(true);
    setError('');
    setRecognizeInfo(null);
    try {
      const base64 = await readImageAsCompressedDataURL(file);
      setPhotoPreview(base64);
      const ocrResult = await api.ocrMedicine(base64);
      // 兼容两种字段名：后端历史上叫 rawText，接口契约里是 raw_text
      const rawText = ocrResult.raw_text || ocrResult.rawText || '';
      await applyRecognition(ocrResult.parsed, rawText, 'ocr');
    } catch (err) {
      setError(err.message || '识别失败，请重试或使用手动录入');
    }
    setLoading(false);
  };

  // 语音录入
  const handleVoice = () => {
    const SpeechRecognition = getSpeechRecognition();
    if (!SpeechRecognition) {
      setError('当前浏览器不支持语音识别，请使用下方的输入框或用 Chrome / Edge 打开');
      return;
    }
    setRecognizing(true);
    setError('');
    const recognition = new SpeechRecognition();
    recognition.lang = 'zh-CN';
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    // 识别超时（15秒无结果自动停止）
    const timeout = setTimeout(() => {
      try { recognition.stop(); } catch (e) {}
      setRecognizing(false);
      setError('未听到语音，请重试，或改用下方的输入框');
    }, 15000);

    recognition.onresult = async (event) => {
      clearTimeout(timeout);
      const text = event.results[0][0].transcript;
      setVoiceRaw(text);
      setRecognizing(false);
      setLoading(true);
      try {
        const parseResult = await api.parseVoice(text);
        await applyRecognition(parseResult.parsed, text, 'voice');
      } catch (err) {
        setError('解析失败：' + (err.message || '请重试'));
      }
      setLoading(false);
    };

    recognition.onerror = (event) => {
      clearTimeout(timeout);
      setRecognizing(false);
      const code = event.error;
      if (code === 'not-allowed' || code === 'service-not-allowed') {
        setError('麦克风权限被拒绝，请在浏览器设置中允许使用麦克风');
      } else if (code === 'no-speech') {
        setError('未检测到语音，请靠近麦克风重试，或改用下方输入框');
      } else if (code === 'audio-capture') {
        setError('没有找到可用的麦克风设备，请改用下方输入框');
      } else if (code === 'network') {
        // Chrome 的语音识别依赖境外服务，国内网络环境下非常容易命中这个错误
        setError('语音识别服务连接失败（浏览器自带的语音服务在当前网络下不可用）。请改用下方的输入框或拍照录入');
      } else {
        setError('语音识别失败：' + code);
      }
    };

    recognition.onend = () => {
      clearTimeout(timeout);
      setRecognizing(false);
    };
    recognition.start();
  };

  // 文字录入：与语音走同一套解析规则。
  // 存在的意义是——浏览器语音不可用时（不支持 / 无麦克风 / 网络不通），
  // 解析能力本身依然可以正常使用，不至于整条链路作废。
  const handleTextParse = async () => {
    const text = textInput.trim();
    if (!text) return;
    setLoading(true);
    setError('');
    setRecognizeInfo(null);
    try {
      const parseResult = await api.parseVoice(text);
      await applyRecognition(parseResult.parsed, text, 'text');
    } catch (err) {
      setError('解析失败：' + (err.message || '请重试'));
    }
    setLoading(false);
  };

  // 手动录入时也做一次核对
  const handleManualValidate = async () => {
    if (!form.name) return;
    setLoading(true);
    try {
      const valResult = await api.validateMedicine(form);
      setValidation(normalizeValidation(valResult));
    } catch (err) {
      setError('核对失败：' + err.message);
    }
    setLoading(false);
  };

  // 校验表单
  const validateForm = () => {
    if (!form.name.trim()) { setError('请输入药品名称'); return false; }
    const dosageStr = String(form.dosage).trim();
    if (!dosageStr) { setError('请输入每次剂量'); return false; }
    // 剂量必须是纯数字：单位由右侧下拉框单独选择，
    // 混在一起会同时导致 input[type=number] 值非法和服务端校验失败
    if (!/^\d+(\.\d{1,2})?$/.test(dosageStr)) {
      setError('剂量请填写数字（最多2位小数），单位请在右侧下拉框中选择');
      return false;
    }
    if (Number(dosageStr) <= 0) { setError('剂量必须大于0'); return false; }
    if (!Array.isArray(form.times) || form.times.length === 0) {
      setError('请至少设置一个提醒时间');
      return false;
    }
    return true;
  };

  // 打开确认弹窗
  const handleOpenConfirm = () => {
    setError('');
    if (!validateForm()) return;
    // 核对判为严重超量时再拦一道，但不阻止保存 —— 最终以药品说明为准
    if (validation && validation.level === 'error') {
      if (!window.confirm('剂量可能严重超量，请再次确认是否与药品说明一致。确定继续保存吗？')) {
        return;
      }
    }
    setShowConfirm(true);
  };

  // 提交
  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (isEdit) {
        await api.updateMedicine(id, form);
        speak(`药品${form.name}已更新`);
      } else {
        await api.createMedicine(form);
        speak(`药品${form.name}已添加`);
      }
      setShowConfirm(false);
      navigate('/medicines');
    } catch (err) {
      setError('保存失败：' + err.message);
    }
    setLoading(false);
  };

  /**
   * 当前录入方式。
   *
   * 三种方式收成一行切换，而不是并排铺三张卡。原来「语音卡 → 拍照卡 →
   * 结果回执卡 → 表单卡」一路纵向排开，语音识别出的药名剂量填在最底下的表单里，
   * 用户必须连着往下拖两屏才看得见结果 —— 这正是要改掉的问题。
   */
  const [mode, setMode] = useState('voice');

  /** 切换录入方式时清掉上一条提示，避免旧错误一直挂在屏幕上 */
  const switchMode = (next) => {
    setMode(next);
    setError('');
  };

  /** 识别失败后的「重试」：按当前方式重来一次，不用让用户往上找回按钮 */
  const handleRetry = () => {
    if (mode === 'voice') { handleVoice(); return; }
    if (mode === 'photo') { fileRef.current?.click(); }
  };

  /** 三种录入方式。语音依赖浏览器的识别接口，不可用时置灰并说明 */
  const MODE_OPTIONS = [
    { value: 'voice', label: '语音录入', available: micSupported, tip: '当前浏览器不支持语音识别' },
    { value: 'photo', label: '拍照识别', available: true, tip: '' },
    { value: 'manual', label: '手填', available: true, tip: '' },
  ];

  return (
    <div className="app-container">
      <h1 className="page-title">{isEdit ? '编辑药品' : '录入药品'}</h1>

      {/* 一句话说完就够，原来这两句占了两行。
          文案压到 15 字是实测结果：应用默认字号是 large（--font-size-small: 18px，
          不是样式表 :root 的 16px 兜底值），原来 16 字的「请与药盒核对」在 18px 下
          差几像素放不下、折成两行；换成同义的「请核对药盒」正好一行。
          换字前请用 scripts/line-count-probe.html 的 ②-2 候选表实测。
          （口径见 components/AIDisclaimer.js —— 核对是规则引擎，不是模型。） */}
      <div className="warning-banner">
        <span className="warning-icon">⚠️</span>
        <span>识别与核对仅供参考，请核对药盒</span>
      </div>

      {/* 录入方式：一行切换，结果就贴在下面 */}
      {!isEdit && (
        <div className="card">
          <div className="mode-switch">
            {MODE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                className={`mode-switch-item ${mode === opt.value ? 'active' : ''}`}
                disabled={!opt.available}
                title={opt.available ? '' : opt.tip}
                onClick={() => switchMode(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* 语音录入 */}
          {mode === 'voice' && (
            <div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <button
                  className={`voice-record-btn ${recognizing ? 'recording' : ''}`}
                  onClick={handleVoice}
                  disabled={loading || !micSupported}
                  style={!micSupported ? { opacity: 0.5 } : undefined}
                >
                  <span className="vicon">{recognizing ? '🔴' : '🎙️'}</span>
                  <span className="vlabel">{recognizing ? '聆听中' : '点一下说话'}</span>
                  <span className="vdot" />
                </button>
                <div className="voice-status">
                  {!micSupported
                    ? '这个浏览器不支持语音识别，改用拍照或手填'
                    : recognizing
                      ? '正在听，请说出药名和吃法'
                      : (voiceRaw ? '可以再点一次重录' : '例如：每天早饭后吃一片阿司匹林')}
                </div>
              </div>

              {/* 不方便说话时的保底通道，压成一行 */}
              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <input
                  className="input-field"
                  style={{ flex: 1, minWidth: 0 }}
                  value={textInput}
                  onChange={e => setTextInput(e.target.value)}
                  placeholder="也可以打一句话，如：晚上8点吃降压药"
                />
                <button
                  className="btn-outline"
                  style={{ width: 'auto', minHeight: 48, padding: '0 16px', flexShrink: 0, whiteSpace: 'nowrap' }}
                  onClick={handleTextParse}
                  disabled={loading || !textInput.trim()}
                >
                  解析
                </button>
              </div>
            </div>
          )}

          {/* 拍照识别 */}
          {mode === 'photo' && (
            <div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={handlePhoto}
              />
              <div
                className={`camera-area ${photoPreview ? 'has-image' : ''}`}
                onClick={() => fileRef.current?.click()}
              >
                {photoPreview ? (
                  <img className="cam-preview" src={photoPreview} alt="药品包装预览" />
                ) : (
                  <div>
                    <div style={{ fontSize: 40, marginBottom: 6 }}>📸</div>
                    <div style={{ fontSize: 'var(--font-size-base)', color: 'var(--text-secondary)' }}>点这里拍照或选图</div>
                    <div style={{ fontSize: 'var(--font-size-small)', color: 'var(--text-light)', marginTop: 4 }}>
                      对准药盒或处方单上的字
                    </div>
                  </div>
                )}
              </div>
              {DEMO_MODE && (
                <div style={{ marginTop: 8, fontSize: 'var(--font-size-small)', color: 'var(--text-light)' }}>
                  演示版在你自己的浏览器里识别图片，首次需下载约 10MB 模型
                </div>
              )}
            </div>
          )}

          {/* 手填：不加额外控件，直接引导去下面的表单 */}
          {mode === 'manual' && (
            <div style={{ padding: '4px 0', color: 'var(--text-secondary)', fontSize: 'var(--font-size-small)' }}>
              不用识别，直接在下面的「药品信息」里填写
            </div>
          )}

          {/*
            识别状态：紧贴切换行显示。
            进行中 / 成功 / 失败三种都有明确反馈，失败还给一个「重试」按钮 ——
            用户不用往回翻找按钮，也不用猜刚才究竟发生了什么。
            这正是原来缺的东西：语音说完没有任何过程提示，结果又落在最底下的表单里。
          */}
          {loading && (
            <div className="recognize-status loading">
              <div className="spinner" />
              <span className="recognize-status-text">
                {mode === 'photo' ? '正在识别图片…' : '正在解析…'}
              </span>
            </div>
          )}
          {!loading && error && (
            <div className="recognize-status error">
              <span className="recognize-status-text">{error}</span>
              {(mode === 'voice' || mode === 'photo') && (
                <button type="button" className="recognize-retry" onClick={handleRetry}>
                  重试
                </button>
              )}
            </div>
          )}
          {!loading && !error && recognizeInfo && recognizeInfo.got.length > 0 && (
            <div className="recognize-status success" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
              <span className="recognize-status-text">
                已填入：{recognizeInfo.got.join('、')}
                {recognizeInfo.missing.length > 0 ? `；没识别到：${recognizeInfo.missing.join('、')}` : ''}
              </span>
              {recognizeInfo.text && (
                <span className="recognize-status-raw">识别原文：{recognizeInfo.text}</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* 手动录入 / 确认表单 */}
      <div className="card">
        <div className="card-title">✍️ 药品信息</div>

        <div className="form-group">
          <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            药品名称 *
            {/* 常用药的引导语搬进气泡。
                原因是算下来它没法在保持药名完整的前提下压成一行：
                「苯磺酸氨氯地平片」这类药名 7~8 个字，16px 下单个 chip 就 140px，
                卡片内容宽只有 307px，一行最多两个。要让引导语和药名同行，
                就只能缩字号或截断药名 —— 用药场景下两者都不能接受。
                所以取最紧凑且不损伤药名的排法：两列网格、3 行装完 6 个药，
                引导语改由这个问号承担。 */}
            <InfoTip title="下面那排常用药是什么？">
              几种常见药，点一下就填进名称栏，省得打字。
              填完还可以改成药盒上印的全名（例如「阿司匹林」→「阿司匹林肠溶片」）。
            </InfoTip>
          </label>
          <input
            className="input-field"
            value={form.name}
            onChange={e => setField('name', e.target.value)}
            placeholder="如：阿莫西林胶囊"
          />
          <div className="chip-grid-meds">
            {COMMON_MEDS.map(name => (
              <button
                key={name}
                type="button"
                className={`chip ${form.name === name ? 'active' : ''}`}
                onClick={() => setField('name', form.name === name ? '' : name)}
              >
                {name}
              </button>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            每次吃几片？ *
            {/* 原来的说明用的是「界面语言的复述」：上面点选、单位在右边——这两件事
                用户看着按钮和下拉框本来就知道。真正只有「最多 2 位小数」是看不见的规则，
                而它又恰好是填错被拦下来时的解释。所以整句折进气泡，页面只留标题。 */}
            <InfoTip title="剂量怎么填？">
              点上面的大数字，1~5 片直接点。吃别的剂量（比如 0.5 片）就填右边输入框，最多 2 位小数。
              单位在右边的下拉框里选。
            </InfoTip>
          </label>
          <div className="dose-preset-row">
            {DOSE_PRESETS.map(n => (
              <button
                key={n}
                type="button"
                className={`dose-btn ${String(form.dosage) === n ? 'active' : ''}`}
                onClick={() => setField('dosage', String(form.dosage) === n ? '' : n)}
              >
                {n}
              </button>
            ))}
          </div>
          <div className="form-row">
            <input
              className="input-field"
              style={{ flex: 1 }}
              type="number"
              min="0.01"
              step="0.01"
              value={form.dosage}
              onChange={e => setField('dosage', e.target.value)}
              placeholder="其他剂量，如 0.5"
            />
            <select
              className="input-field"
              /* 原来写死 width:100（照 20px 字号量的），超大号下「毫升」加下拉箭头装不下 */
              style={{ flexShrink: 0, minWidth: 92, maxWidth: '45%' }}
              value={form.unit}
              onChange={e => setField('unit', e.target.value)}
            >
              {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
        </div>

        {/* 服用频次：5 个选项排成整齐的 3 + 2 两行。
            用 flex-wrap 会得到「3 个宽的 + 2 个窄的」，宽度对不齐；
            grid 固定 3 列后，两行的按钮宽度一致。
            用户明确表过态：这一组两行可以接受，整齐比省一行重要。 */}
        <div className="form-group">
          <label className="form-label">服用频次</label>
          <div className="freq-grid">
            {FREQUENCIES.map(f => (
              <button
                key={f}
                style={{
                  borderRadius: 8,
                  background: form.frequency === f ? 'var(--primary-color)' : 'var(--border-color)',
                  color: form.frequency === f ? 'white' : 'var(--text-primary)',
                  fontWeight: 600,
                  minHeight: 48,
                }}
                onClick={() => setField('frequency', f)}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* 服用方式：4 个按钮等分一行（原来是 12px/18px 内边距，
            4 个共 328px，比卡片内容宽 307px 多出 21px，于是变成 3 个一行、
            第 4 个孤零零掉到下一行）。
            后端早就支持 method 字段，但界面一直没有入口，
            导致识别出的「饭后」无处安放、也无法参与提醒播报。 */}
        <div className="form-group">
          <label className="form-label">服用方式</label>
          <div className="method-row">
            {METHODS.map(m => {
              const selected = form.method === m;
              return (
                <button
                  key={m}
                  style={{
                    borderRadius: 8,
                    background: selected ? 'var(--primary-color)' : 'var(--border-color)',
                    color: selected ? 'white' : 'var(--text-primary)',
                    fontWeight: 600,
                    minHeight: 48,
                  }}
                  onClick={() => {
                    const next = selected ? '' : m;
                    setField('method', next);
                    // 注意事项为空时顺手补一句，省得老人再打一遍字
                    if (next && !form.notes.trim()) setField('notes', `${next}服用`);
                    if (!next && /^..服用$/.test(form.notes.trim())) setField('notes', '');
                  }}
                >
                  {m}
                </button>
              );
            })}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            什么时间吃？ *
            <InfoTip title="怎么选时间？">
              可以选多个（如早晚各一次），再点一下取消。其他时间用下面的自定义。
            </InfoTip>
          </label>
          <div className="chip-row chip-row-times">
            {TIME_PRESETS.map(p => {
              const active = form.times.includes(p.time);
              return (
                <button
                  key={p.time}
                  type="button"
                  className={`chip chip-time ${active ? 'active' : ''}`}
                  onClick={() => toggleTime(p)}
                >
                  {p.label}
                  <small>{p.time}</small>
                </button>
              );
            })}
          </div>
          <div className="form-row">
            <input
              type="time"
              className="input-field"
              style={{ flex: 1, minWidth: 0 }}
              value={form.times[form.times.length - 1] || '08:00'}
              onChange={e => {
                const newTimes = [...form.times];
                newTimes[newTimes.length - 1] = e.target.value;
                setField('times', newTimes);
              }}
            />
            <button
              type="button"
              className="btn-primary btn-sm"
              style={{ minHeight: 48, width: 'auto', padding: '0 20px', flexShrink: 0, whiteSpace: 'nowrap' }}
              onClick={() => setField('times', [...form.times, '12:00'])}
            >
              ➕ 添加
            </button>
          </div>
          <div className="time-tags">
            {form.times.map((time, i) => (
              <span key={i} className="time-tag">
                {time}
                {form.times.length > 1 && (
                  <span
                    className="tag-del"
                    onClick={() => setField('times', form.times.filter((_, idx) => idx !== i))}
                  >
                    ×
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">注意事项（可选）</label>
          <textarea
            className="input-field"
            rows={3}
            value={form.notes}
            onChange={e => setField('notes', e.target.value)}
            placeholder="如：饭后服用"
          />
        </div>

        <button
          className="btn-outline"
          style={{ minHeight: 48, marginBottom: 12 }}
          onClick={handleManualValidate}
          disabled={loading || !form.name}
        >
          {loading ? '核对中...' : '🔍 核对药品信息'}
        </button>

        <button
          className="btn-primary btn-lg"
          style={{ marginTop: 8 }}
          onClick={handleOpenConfirm}
          disabled={loading}
        >
          ✅ 核对并保存药品
        </button>
      </div>

      {/* 校验结果 */}
      {validation && (
        <div className="card">
          <div className="card-title">🔍 核对结果</div>
          {validation.errors.length > 0 && validation.errors.map((msg, i) => (
            <p key={`e${i}`} style={{ color: 'var(--danger-color)', marginBottom: 8, lineHeight: 1.7, fontWeight: 600 }}>
              ⛔ {msg}
            </p>
          ))}
          {validation.warnings.length > 0 ? (
            validation.warnings.map((msg, i) => (
              <p key={`w${i}`} style={{ color: 'var(--warning-color)', marginBottom: 8, lineHeight: 1.7 }}>
                ⚠️ {msg}
              </p>
            ))
          ) : (
            validation.errors.length === 0 && (
              <p style={{ color: 'var(--success-color)' }}>✅ 药品信息看起来正常</p>
            )
          )}
          {validation.suggestions.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <p style={{ fontWeight: 600 }}>建议：</p>
              {validation.suggestions.map((msg, i) => (
                <p key={`s${i}`} style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-small)', lineHeight: 1.7 }}>
                  💡 {msg}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 确认弹窗 */}
      {showConfirm && (
        <div className="overlay" onClick={() => setShowConfirm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">🔍 请核对录入信息</div>
            <div className="warning-banner" style={{ marginBottom: 16 }}>
              <span className="warning-icon">⚠️</span>
              <span>请仔细与药品包装核对，确认无误后再保存！</span>
            </div>
            <div style={{ fontSize: 'var(--font-size-base)', lineHeight: 2 }}>
              <div><b>药品：</b>{form.name}</div>
              <div><b>剂量：</b>{form.dosage} {form.unit}</div>
              <div><b>频次：</b>{form.frequency}</div>
              {form.method && <div><b>服用方式：</b>{form.method}</div>}
              <div><b>提醒时间：</b>{form.times.join('、')}</div>
              {form.notes && <div><b>注意事项：</b>{form.notes}</div>}
            </div>
            <div className="modal-actions">
              <button className="btn-outline" style={{ minHeight: 48 }} onClick={() => setShowConfirm(false)}>
                取消
              </button>
              <button className="btn-primary" style={{ minHeight: 48 }} onClick={handleSubmit} disabled={loading}>
                {loading ? '保存中...' : '✅ 确认保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      <AIDisclaimer />
    </div>
  );
}
